import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { isLowerEnv } from '@/config/utils';
import { AppSubscriber } from '@/containers/Subscriber/AppSubscriber';
import { RootVendors } from '@/containers/Subscriber/RootVendors';
import { Layout } from '@/Layout';
import { ProvideDefaultPaths } from '@/router/ProvideDefaultPaths';
import { Calendar } from '@/screens/Calendar';
import { ChatScreen } from '@/screens/Chat';
import { DiagnosticsScreen } from '@/screens/Diagnostics';
import { AnalystMain } from '@/screens/GroupMain';
import { IFrameTest } from '@/screens/IFrameTest/IFrameTest';
import { OAuth2, OAuth2Logout } from '@/screens/Inbound';
import { OAuth2Login } from '@/screens/Inbound/Auth.OAuth2.Login';
import { LoginScreen } from '@/screens/Login';
import { LogoutScreen } from '@/screens/Logout';
import { ProjectDashboard } from '@/screens/Project.Dashboard/Project.Dashboard';
import { ProjectDetails } from '@/screens/Project.Details';
import { ProjectEntities } from '@/screens/Project.Entities';
import { ProjectFiles } from '@/screens/Project.Files';
import { ProjectHighlights } from '@/screens/Project.Highlights';
import { ProjectMain } from '@/screens/Project.Main';
import { ProjectSummary } from '@/screens/Project.Summary';
import { ProjectResponses } from '@/screens/Project.Responses/Project.Responses';
import { QuantAnalysis } from '@/screens/Project.QuantAnalysis';
import { ProjectTags } from '@/screens/Project.Tags';
import { BrandNotFound, WorkspaceBrand } from '@/screens/Workspace.Brand';
import { BrandTabFiles } from '@/screens/Workspace.Brand/Tab.Files';
import { BrandTabProjects } from '@/screens/Workspace.Brand/Tab.Projects';
import { BrandTabProjectTracker } from '@/screens/Workspace.Brand/Tab.ProjectTracker';
import { WorkspaceFile, FileNotFound } from '@/screens/Workspace.File';
import { FileTabDetails } from '@/screens/Workspace.File/File.Tab.Details';
import { FileTabPreview } from '@/screens/Workspace.File/File.Tab.Preview';
import { FileTabSummary } from '@/screens/Workspace.File/File.Tab.Summary';
import { WorkspaceFilesTable } from '@/screens/Workspace.FilesTable';
import { WorkspaceFolder } from '@/screens/Workspace.Folder';
import FolderNotFound from '@/screens/Workspace.Folder/Folder.NotFound';
import { FolderTabDetails } from '@/screens/Workspace.Folder/Tab.Details';
import { FolderTabFiles } from '@/screens/Workspace.Folder/Tab.Files';
import { WorkspaceTranscript } from '@/screens/Workspace.Transcript/Workspace.Transcript';
import { WorkspaceLayout } from '@/screens/Workspace/Workspace.Layout';
import { Search as WorkspaceSearch } from '@/screens/WorkspaceGlobalSearch';
import SearchTabAll from '@/screens/WorkspaceGlobalSearch/Search.Tab.All';
import SearchTabFiles from '@/screens/WorkspaceGlobalSearch/Search.Tab.Files';
import SearchTabFolders from '@/screens/WorkspaceGlobalSearch/Search.Tab.Folders';
import SearchTabBrands from '@/screens/WorkspaceGlobalSearch/Search.Tab.Brands';
import SearchTabDashboards from '@/screens/WorkspaceGlobalSearch/Search.Tab.Dashboards';
import SearchTabQuantAnalyses from '@/screens/WorkspaceGlobalSearch/Search.Tab.QuantAnalyses';
import { AppInitializedGuard } from './Guard.App';
import { AuthenticatedGuard, UnauthenticatedGuard } from './Guard.Authentication';
import { BrandingInitializedGuard } from './Guard.Branding';
import { UserDisabled } from '@/screens/UserDisabled/UserDisabled';
import { getAppDefaultPaths } from './utils';
import { TrinityEdgeAuthCallback } from '@/screens/Inbound/Auth.TrinityEdge';
import { Home } from '@/screens/Home/Home';
import { PageScreen } from '@/screens/Page/Page';
import SearchTabProjects from '@/screens/WorkspaceGlobalSearch/Search.Tab.Projects';
import SearchTabTranscripts from '@/screens/WorkspaceGlobalSearch/Search.Tab.Transcripts';
import { SettingsScreen, SettingsAccountScreen } from '@/screens/Settings';
import { lazy, Suspense } from 'react';

const LazySegmentationScreen = lazy(() =>
  import('@/screens/Segmentation').then((m) => ({ default: m.SegmentationScreen }))
);

const LazySegmentProfileScreen = lazy(() =>
  import('@/screens/Segmentation').then((m) => ({ default: m.SegmentProfileScreen }))
);

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);

function buildRoutes() {
  const defaultPaths = getAppDefaultPaths();

  const mainRoute = {
    path: '/',
    element: (
      <RootVendors>
        <AppSubscriber>
          <ProvideDefaultPaths>
            <Layout />
          </ProvideDefaultPaths>
        </AppSubscriber>
      </RootVendors>
    ),
    children: [
      { index: true, element: <Navigate to="/home" /> },
      {
        path: '_/auth/oauth2',
        children: [
          { path: 'logout', element: <OAuth2Logout /> },
          { path: ':identifier', element: <OAuth2 /> },
        ],
      },
      {
        path: '_/auth/trinity-edge-callback',
        element: <TrinityEdgeAuthCallback />,
      },
      {
        path: defaultPaths.login.substring(1),
        element: <OAuth2Login />,
      },
      {
        path: 'user-disabled',
        element: <UserDisabled />,
      },
      {
        path: 'home',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <Home />
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
      },
      {
        path: 'pages/:pageId',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <PageScreen />
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
      },
      {
        path: 'main',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <AnalystMain />
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
      },
      {
        path: 'calendar',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <Calendar />
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
      },
      {
        path: 'chat',
        children: [
          {
            path: ':chatIdentifier?',
            element: (
              <AuthenticatedGuard>
                <AppInitializedGuard>
                  <ChatScreen />
                </AppInitializedGuard>
              </AuthenticatedGuard>
            ),
          },
        ],
      },
      {
        path: 'diagnostics',
        element: <DiagnosticsScreen />,
      },
      {
        path: defaultPaths.manualLogin.substring(1),
        element: (
          <BrandingInitializedGuard>
            <UnauthenticatedGuard>
              <LoginScreen />
            </UnauthenticatedGuard>
          </BrandingInitializedGuard>
        ),
      },
      {
        path: 'logout',
        element: <LogoutScreen />,
      },
      {
        path: 'projects/:projectId',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <ProjectMain />
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
        children: [
          { index: true, element: <Navigate to="entities" replace /> },
          { path: 'entities', element: <ProjectEntities /> },
          { path: 'files', element: <ProjectFiles /> },
          { path: 'details', element: <ProjectDetails /> },
          { path: 'dashboard', element: <ProjectDashboard /> },
          { path: 'quantanalysis', element: <QuantAnalysis /> },
          { path: 'quantanalysis/:quantAnalysisId', element: <QuantAnalysis /> },
          { path: 'quantanalysis/:quantAnalysisId/:versionNumber', element: <QuantAnalysis /> },
          { path: 'tags/:tagId?', element: <ProjectTags /> },
          { path: 'highlights', element: <ProjectHighlights /> },
          { path: 'summary', element: <ProjectSummary /> },
          { path: 'responses', element: <ProjectResponses /> },
        ],
      },
      {
        path: 'project/:projectId',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <ProjectMain />
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          { path: 'dashboard', element: <ProjectDashboard /> },
        ],
      },
      {
        path: 'workspaces',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <WorkspaceLayout />
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
        children: [
          {
            index: true,
            element: <WorkspaceFilesTable />,
          },
          {
            path: 'search',
            element: <WorkspaceSearch />,
            children: [
              { index: true, element: <RedirectToAllTab /> },
              { path: 'all', element: <SearchTabAll /> },
              { path: 'files', element: <SearchTabFiles /> },
              { path: 'folders', element: <SearchTabFolders /> },
              { path: 'brands', element: <SearchTabBrands /> },
              { path: 'dashboards', element: <SearchTabDashboards /> },
              { path: 'quantanalyses', element: <SearchTabQuantAnalyses /> },
              { path: 'projects', element: <SearchTabProjects /> },
              { path: 'transcripts', element: <SearchTabTranscripts /> },
            ],
          },
          {
            path: 'transcripts/:transcriptId',
            element: <WorkspaceTranscript />,
          },
          {
            path: 'folders/:folderId',
            element: <WorkspaceFolder />,
            children: [
              { index: true, element: <Navigate to="files" replace /> },
              { path: 'files', element: <FolderTabFiles /> },
              { path: 'details', element: <FolderTabDetails /> },
            ],
          },
          {
            path: 'folders/not-found',
            element: <FolderNotFound />,
          },
          {
            path: 'files/not-found',
            element: <FileNotFound />,
          },
          {
            path: 'files/:fileId',
            element: <WorkspaceFile />,
            children: [
              { index: true, element: <RedirectToFilePreview /> },
              { path: 'preview', element: <FileTabPreview /> },
              { path: 'summary', element: <FileTabSummary /> },
              { path: 'details', element: <FileTabDetails /> },
            ],
          },
          {
            path: 'brands/:brandId',
            element: <WorkspaceBrand />,
            children: [
              { path: 'files', element: <BrandTabFiles /> },
              { path: 'projects', element: <BrandTabProjects /> },
              { path: 'tracker', element: <BrandTabProjectTracker /> },
            ],
          },
          {
            path: 'brands/not-found',
            element: <BrandNotFound />,
          },
        ],
      },
      {
        path: 'segmentation',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <Suspense fallback={null}>
                <LazySegmentationScreen />
              </Suspense>
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
      },
      {
        path: 'segmentation/segment/:pageId',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <Suspense fallback={null}>
                <LazySegmentProfileScreen />
              </Suspense>
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
      },
      {
        path: 'settings',
        element: (
          <AuthenticatedGuard>
            <AppInitializedGuard>
              <SettingsScreen />
            </AppInitializedGuard>
          </AuthenticatedGuard>
        ),
        children: [
          { index: true, element: <Navigate to="account" replace /> },
          { path: 'account', element: <SettingsAccountScreen /> },
        ],
      },
      {
        path: '*',
        element: <Navigate to={defaultPaths.home} replace />,
      },
    ],
  };

  const routes = [];

  if (isLowerEnv()) {
    routes.push({
      path: '/_iframe',
      element: <IFrameTest />,
    });
  }

  routes.push(mainRoute);
  return routes;
}

export function createRouter() {
  return sentryCreateBrowserRouter(buildRoutes());
}

function RedirectToFilePreview() {
  const location = useLocation();
  const search = location.search;
  return <Navigate to={`preview${search}`} replace />;
}

function RedirectToAllTab() {
  const location = useLocation();
  const search = location.search;
  return <Navigate to={`all${search}`} replace />;
}
